import { ChangeEvent, SetStateAction, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next';


import Head from 'next/head';
import Header from './../../../components/Header';
import Footer from './../../../components/Footer';
import { server } from '../../../config/index';

type SampleQuestions = {
    [key in keyof typeof templates]: string[];
};

type Template = {
    title: string;
    about: string;
    fields: {
        [key: string]: {
            explanation: string;
            linkingWords: string[];
            example: string;
        };
    };
};


const templates: { [templateName: string]: Template } = {
    opinion: {
        title: "Opinion Essay",
        about: `Opinion essays require you to express your viewpoint on a given topic. The prompt often starts with phrases like "To what extent do you agree or disagree?" or "Do you agree or disagree?" You need to clearly state your opinion and support it with reasons and examples.`,
        fields: {
            'Introduction': {
                explanation: 'Here, introduce your topic and state your opinion. Use interesting facts or statistics to catch the reader\'s attention.',
                linkingWords: [
                    'In my opinion,',
                    'I believe that,',
                    'I think that,',
                    'It is my opinion that,',
                    'I feel that,',
                    'I would argue that,',
                ],
                example: 'In my opinion, education is the key to success.',
            },
            'Main Point 1': {
                explanation: 'Explain your first argument that supports your opinion. Be clear and concise.',
                linkingWords: [
                    'Additionally,',
                    'Moreover,',
                    'Furthermore,',
                    'In addition,',
                    'Also,',
                ],
                example: 'Education helps people to get better jobs.',
            },
            'Main Point 2': {
                explanation: 'Explain your second argument that supports your opinion. Be clear and concise.',
                linkingWords: [
                    'Additionally,',
                    'Moreover,',
                    'Furthermore,',
                    'In addition,',
                    'Also,',
                ],
                example: 'Education helps people to be more informed citizens.',
            },
            'Conclusion': {
                explanation: 'Summarize your main points and restate your opinion.',
                linkingWords: [
                    'In conclusion,',
                    'To sum up,',
                    'In summary,',
                    'All in all,',
                    'To put it simply,',
                ],
                example: 'In conclusion, I believe that education is the key to success.',
            },
        },
    },
    discussion: {
        title: "Discussion Essay",
        about: `Discussion essays require you to discuss both sides of an issue. The prompt often starts with phrases like "Some people believe that..." or "Others believe that...". You need to clearly state both sides of the issue and then provide evidence to support both sides. In the conclusion, you need to state your own opinion on the issue.`,
        fields: {
            'Introduction': {
                explanation: 'Here, introduce your topic and state both sides of the issue. Use interesting facts or statistics to catch the reader\'s attention.',
                linkingWords: [
                    'Some people believe that,',
                    'Others believe that,',
                    'There are those who believe that,',
                    'There are also those who believe that,',
                ],
                example: 'Some people believe that the government should provide free education to all citizens. Others believe that the government should not provide free education.',
            },
            'Main Point 1': {
                explanation: 'Explain the first side of the issue. Be clear and concise.',
                linkingWords: [
                    'On the one hand,',
                    'On the other hand,',
                    'In addition,',
                    'Moreover,',
                    'Furthermore,',
                ],
                example: 'Those who believe that the government should provide free education argue that it is a fundamental human right.',
            },
            'Main Point 2': {
                explanation: 'Explain the second side of the issue. Be clear and concise.',
                linkingWords: [
                    'On the one hand,',
                    'On the other hand,',
                    'In addition,',
                    'Moreover,',
                    'Furthermore,',
                ],
                example: 'Those who believe that the government should not provide free education argue that it is too expensive.',
            },
            'Conclusion': {
                explanation: 'Summarize your main points and state your own opinion on the issue.',
                linkingWords: [
                    'In conclusion,',
                    'To sum up,',
                    'In summary,',
                    'All in all,',
                    'To put it simply,',
                ],
                example: 'In conclusion, I believe that the government should provide free education to all citizens.',
            },
        },
    },
    advantagesAndDisadvantages: {
        title: "Advantages and Disadvantages Essay",
        about: `Advantages and Disadvantages essays require you to discuss the pros and cons of a particular issue or situation. The question might say "What are the advantages and disadvantages of?" You need to outline both the positive and negative aspects in a balanced manner.`,
        fields: {
            'Introduction': {
                explanation: 'State the topic and give a brief overview of the advantages and disadvantages.',
                linkingWords: [
                    'This essay will discuss the advantages and disadvantages of,',
                    'In this essay, I will discuss the pros and cons of,',
                    'This essay will explore the benefits and drawbacks of,',
                ],
                example: 'This essay will discuss the advantages and disadvantages of working from home.',
            },
            'Advantages': {
                explanation: 'List the advantages of the topic. Provide evidence to support each advantage.',
                linkingWords: [
                    'One advantage of,',
                    'Another advantage of,',
                    'A third advantage of,',
                ],
                example: 'One advantage of working from home is that you can save money on commuting costs.',
            },
            'Disadvantages': {
                explanation: 'List the disadvantages of the topic. Provide evidence to support each disadvantage.',
                linkingWords: [
                    'One disadvantage of,',
                    'Another disadvantage of,',
                    'A third disadvantage of,',
                ],
                example: 'One disadvantage of working from home is that it can be difficult to stay motivated.',
            },
            'Conclusion': {
                explanation: 'Summarize the advantages and disadvantages and state your opinion on the topic.',
                linkingWords: [
                    'In conclusion,',
                    'To sum up,',
                    'In summary,',
                    'All in all,',
                    'To put it simply,',
                ],
                example: 'In conclusion, I believe that the advantages of working from home outweigh the disadvantages.',
            },
        },
    },
    solution: {
        title: "Solution Essay",
        about: `Solution essays require you to propose a solution to a problem. The prompt often starts with phrases like "What is the best solution to?". You need to clearly state the problem and then propose a solution. In the conclusion, you need to explain why your solution is the best.`,
        fields: {
            'Introduction': {
                explanation: 'Here, introduce your topic and state the problem. Use interesting facts or statistics to catch the reader\'s attention.',
                linkingWords: [
                    'The problem with,',
                    'One of the problems with,',
                    'There is a problem with,',
                ],
                example: 'The problem with traffic congestion in big cities is that it can cause delays, pollution, and stress.',
            },
            'Solution 1': {
                explanation: 'Explain your first solution to the problem. Be clear and concise.',
                linkingWords: [
                    'One solution to,',
                    'Another solution to,',
                    'A possible solution to,',
                ],
                example: 'One solution to traffic congestion is to build more roads and highways.',
            },
            'Solution 2': {
                explanation: 'Explain your second solution to the problem. Be clear and concise.',
                linkingWords: [
                    'One solution to,',
                    'Another solution to,',
                    'A possible solution to,',
                ],
                example: 'Another solution to traffic congestion is to improve public transportation.',
            },
            'Conclusion': {
                explanation: 'Summarize your main points and explain why your solution is the best.',
                linkingWords: [
                    'In conclusion,',
                    'To sum up,',
                    'In summary,',
                    'All in all,',
                    'To put it simply,',
                ],
                example: 'In conclusion, I believe that the best solution to traffic congestion is to improve public transportation.',
            },
        },
    }, directQuestion: {
        title: "Direct Question Essay",
        about: `Direct Question essays, you are given one or more specific questions to answer. The prompt will pose direct questions like "Why is this the case?" or "What do you think the reasons are for?" Your task is to answer these questions fully and support your answers with reasons and examples.`,
        fields: {
            'Introduction': {
                explanation: 'State the question that will be answered in the essay.',
                linkingWords: [
                    'This essay will answer the question,',
                    'In this essay, I will discuss the answer to,',
                    'This essay will explore the reasons for,',
                ],
                example: 'This essay will answer the question: Should the government provide free healthcare to all citizens?',
            },
            'Answer': {
                explanation: 'Answer the question by providing evidence and reasoning.',
                linkingWords: [
                    'In my opinion,',
                    'I believe that,',
                    'I think that,',
                ],
                example: 'In my opinion, the government should provide free healthcare to all citizens. There are many reasons for this. First, healthcare is a basic human right. Everyone should have access to quality healthcare, regardless of their income. Second, providing free healthcare would improve the overall health of the population. People would be more likely to seek preventive care and treatment for illnesses, which would lead to fewer deaths and disabilities. Third, providing free healthcare would save the government money in the long run. By preventing illnesses and treating them early, the government would save money on expensive hospital stays and long-term care.',
            },
            'Conclusion': {
                explanation: 'Summarize your answer and restate your opinion.',
                linkingWords: [
                    'In conclusion,',
                    'To sum up,',
                    'In summary,',
                    'All in all,',
                    'To put it simply,',
                ],
                example: 'In conclusion, I believe that the government should provide free healthcare to all citizens. It is the right thing to do, it would improve the health of the population, and it would save the government money in the long run.',
            },
        },
    },
};

const sampleQuestions: SampleQuestions = {
    opinion: [
        "Education is often considered the key to success. To what extent do you agree or disagree?",
        "Some people argue that it is more important to have an enjoyable job than to earn a lot of money. Do you agree or disagree?",
        "Some people believe that there should be fixed punishments for each type of crime. Others, however, argue that the circumstances of an individual crime, and the motivation for committing it, should always be taken into account when deciding on the punishment. Discuss both views and give your own opinion."
    ],
    discussion: [
        "Some people believe that the government should provide free education to all citizens. Others believe that education should be paid for by those who use it. Discuss both views and give your own opinion.",
        "Some people think that all university students should study whatever they like. Others believe that they should only be allowed to study subjects that will be useful in the future, such as those related to science and technology. Discuss both views and give your own opinion.",
        "Some people say it is important to keep your home and your workplace tidy, with everything organized and in the correct place. What is your opinion about this?"
    ],
    advantagesAndDisadvantages: [
        "Working from home has become more popular in recent years. Discuss the advantages and disadvantages of working from home.",
        "Nowadays many people choose to be self-employed, rather than to work for a company or organization. Discuss the advantages and disadvantages of being self-employed.",
        "In some countries, more and more people are becoming interested in finding out about the history of the house or building they live in. Discuss the advantages and disadvantages of this trend."
    ],
    solution: [
        "Traffic congestion in big cities is a major problem. What do you think are the best solutions to this problem?",
        "Many people believe that social networking sites (such as Facebook) have had a huge negative impact on both individuals and society. What solutions can you suggest?",
        "The number of people who are at risk of serious health problems due to being overweight is increasing. What solutions can you suggest for this problem?"
    ],
    directQuestion: [
        "Should the government provide free healthcare to all citizens? What do you think the reasons are for your answer?",
        "Some people think that in the modern world we have become more dependent on each other, while others think that people are now more independent. Why do you think this is the case?",
        "Some people think that it is better to educate boys and girls in separate schools. Others, however, believe that boys and girls benefit more from attending mixed schools. Why do you think this is the case?"
    ],
};

export const getStaticPaths: GetStaticPaths = async () => {
    const templateNames = Object.keys(templates);

    const paths = templateNames.map((name) => ({
        params: { template: name },
    }));

    return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async (context) => {
    const templateName = context.params?.template as string || 'opinion';
    const template = templates[templateName];

    return {
        props: {
            template,
            templateName
        },
    };
};


const EssayTemplate = ({ template, templateName }: InferGetStaticPropsType<typeof getStaticProps>) => {

    const [currentQuestion, setCurrentQuestion] = useState(sampleQuestions[templateName][0]);
    const [inputs, setInputs] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        setCurrentQuestion(sampleQuestions[templateName as keyof typeof templates][0]); // reset question when template changes
        setInputs(Object.keys(template.fields).reduce((obj, key) => ({ ...obj, [key]: '' }), {}));
    }, [templateName]);

    const handleInputChange = (field: string, event: ChangeEvent<HTMLTextAreaElement>) => {
        setInputs({ ...inputs, [field]: event.target.value });
    };

    const handleQuestionChange = (event: { target: { value: SetStateAction<string>; }; }) => {
        setCurrentQuestion(event.target.value);
        setInputs(Object.keys(template.fields).reduce((obj, key) => ({ ...obj, [key]: '' }), {}));
    };


    return (
        <div className="flex flex-col min-h-screen">
            <Head>
                <title>IELTS {template.title} Templates for Writing Task 2 with Band 9 Structure</title>
                <meta name="description" content={`A helpful IELTS ${template.title} Template with a free online tool to structure your essay and get tips on what to include in each section.`} />

                <meta property="og:type" content="website" />
                <meta property="og:title" content={`IELTS ${template.title} Templates for Writing Task 2 with Band 9 Structure`} />
                <meta property="og:description" content={`A helpful IELTS ${template.title} Template with a free online tool to structure your essay and get tips on what to include in each section.`} />
                <meta property="og:image" content={`${server}/templates.jpg`} />
                <meta property="og:image:type" content="image/jpeg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta property="twitter:domain" content={`${server}`} />
                <meta name="twitter:title" content={`IELTS ${template.title} Templates for Writing Task 2 with Band 9 Structure`} />
                <meta name="twitter:description" content={`A helpful IELTS ${template.title} Template with a free online tool to structure your essay and get tips on what to include in each section.`} />
                <meta name="twitter:image" content={`${server}/templates.jpg`} />

            </Head>

            <Header />

            <main className="flex-grow max-w-3xl mx-auto pt-2 pb-10 px-2">

                <nav className="mb-4">
                    {Object.keys(templates).map(key => (
                        <Link legacyBehavior href={`/tools/essay-template/${key}`} key={key} >
                            <a className='inline-block text-blue-800 px-2 py-1 mr-2 my-1 text-xs rounded underline'>{templates[key].title}</a>
                        </Link>
                    ))}
                </nav>

                <h1 className="text-xl font-semibold mt-5 mb-2 text-center">IELTS {template.title} Template</h1>
                <div className='prose whitespace-pre-wrap bg-slate-100 p-2 my-3 text-sm rounded'><b>What is {template.title}?</b> {template.about}</div>

                <div className="mb-6 pt-5">
                    <h2 className="text-lg font-semibold mb-2">Sample Question</h2>
                    <select
                        className="w-full p-2 rounded border border-gray-300 text-xs"
                        value={currentQuestion}
                        onChange={handleQuestionChange}
                    >
                        {sampleQuestions[templateName].map((question, index) => {
                            return (<option value={question} key={index}>{index + 1}. {question}</option>)
                        })}
                    </select>
                    <div className='bg-yellow-100 p-2 text-sm mt-2'>
                        {currentQuestion}
                    </div>
                </div>

                {Object.entries(template.fields).map(([field, { explanation, linkingWords, example }]: any) => (
                    <div key={field} className="mb-4">
                        <h2 className="text-lg font-semibold mb-1">{field}</h2>
                        <textarea
                            className="w-full h-24 p-2 border border-gray-300 rounded shadow-sm"
                            value={inputs[field]}
                            onChange={(e) => handleInputChange(field, e)}
                            placeholder={`${explanation}. Example: ${example}`}
                        />
                        <p className="text-xs text-gray-500 mt-1"><strong>Linking words:</strong> {linkingWords.join(' ')}</p>
                    </div>
                ))}

                <h2 className="text-xl font-semibold mt-4 mb-2">Your Essay:</h2>
                {Object.keys(template.fields).map((field) => (
                    <div key={field} className="mb-2 p-2 bg-slate-100 rounded shadow-sm">
                        <h3 className="font-semibold mb-1">{field}:</h3>
                        <p className="text-base">
                            {inputs[field]}
                        </p>
                    </div>
                ))}

                <div className='text-normal pt-10'>
                    <p className='text-xl'>FAQ</p>
                    <p><b>What is this?</b> <br />This free online interactive app will help you to learn optmimal structure for IELTS writing task 2.</p>
                    <br />
                    <p><b>How to use this essay template?</b><br /> Select Essay Type, sample question and fill the blocks, in the end you will see the combined essay.</p>
                    <br />
                    <p><b>Why essay structure this important?</b><br /> IELTS essay structure is evaluated as part of the Coherence and Cohesion rubric, which contributes 25% to the overall band. </p>
                </div>
            </main>


            <Footer />
        </div>
    );
};

export default EssayTemplate;
